"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { Icon } from "./Icon";
import { cn } from "@/lib/utils";

export type SelectOption = {
  value: string;
  label: string;
};

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
  disabled?: boolean;
}

export function Select({
  value,
  options,
  onChange,
  placeholder = "Select…",
  className = "",
  "aria-label": ariaLabel,
  disabled,
}: SelectProps) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          "inline-flex h-9 min-w-[9.5rem] items-center justify-between gap-2 rounded-lg border border-secondary/15 bg-card px-3 text-sm text-secondary outline-none transition hover:border-secondary/30 focus-visible:ring-2 focus-visible:ring-primary/40 data-[placeholder]:text-muted cursor-pointer disabled:cursor-not-allowed data-[disabled]:cursor-not-allowed",
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <Icon name="chevron-down" size={14} className="text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-(--radix-select-content-available-height) overflow-hidden rounded-xl border border-secondary/10 bg-card shadow-lg"
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-card text-muted">
            <Icon name="chevron-up" size={14} />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="max-h-72 overflow-y-auto p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="relative flex cursor-pointer select-none items-center rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-secondary/5 data-[state=checked]:font-medium"
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-card text-muted">
            <Icon name="chevron-down" size={14} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
